import classNames from 'classnames';
import { useCallback, useMemo } from 'react';
import { useRecoilValue } from 'recoil';

import { UserRolesPermissions, loginState } from 'global-stores';

import { SUB_SIDEBAR, pathForTab } from 'routes';
import { useUserRoles } from 'views/routes-children';
import './toggle-route.scss';

interface SUB_ROUTE {
	ROUTE: string;
	TITLE: string;
	ICON: string;
}
interface ROUTE {
	ROUTE: string;
	TITLE: string;
	ICON: string;
	TAB_INDEX: number;
	SUB_ROUTE: SUB_ROUTE[];
}
interface AppProps {
	index: number;
	route: ROUTE;
	handleChangeTab: any;
	pathname: string;
	openToggle: string;
	setOpenToggle: any;
	setisActive: any;
	windowSize?: number[];
	/**
	 * Right-aligned counts on the SUB-ROUTE rows, keyed by their TITLE
	 * ("Onboarding Flow", "Sessions", …). A missing key, or null, draws
	 * nothing — which is not the same as 0. `plus` decides whether the number
	 * reads as an arrival ("+5 new in the last 24h") or as a queue ("5
	 * waiting"), because those are different facts and a bare number cannot
	 * tell them apart.
	 */
	badges?: Record<
		string,
		| {
				/** change in the last 24 hours — signed, neon blue */
				delta?: number | null;
				/** true when the count was capped by a page limit ("+20+") */
				deltaMore?: boolean;
				/** work waiting on a human — a queue, amber pill */
				pending?: number | null;
		  }
		| undefined
	>;
}

/**
 * A badge figure, short enough to fit the pill.
 *
 * Transaction Monitoring ingests at a volume the other rows never see — a busy
 * book is millions of transactions, and "1243718" in a 40px pill either
 * overflows the sidebar or gets clipped into a number that is simply WRONG to
 * read. So thousands become K and millions M, at one decimal.
 *
 * Below 1000 the number is shown exactly. That is deliberate: those are the
 * figures a person acts on individually ("62 alerts open"), and rounding them
 * would be a loss for no gain in space.
 *
 * A trailing ".0" is dropped — "55K" rather than "55.0K" — because the zero
 * claims a precision the rounding has already discarded.
 *
 * TRUNCATED, NOT ROUNDED UP. `Math.floor` on the tenth, so 1999 reads "1.9K"
 * and never "2.0K". A badge that rounds up can show "1.0K" for 999 items,
 * which is a number the underlying screen will then contradict.
 *
 * The exact value always survives in the `title`, so the precision is one hover
 * away and nothing is truly hidden.
 */
export const compactCount = (n: number): string => {
	const abs = Math.abs(n);
	if (abs < 1000) return String(n);
	const unit = abs >= 1_000_000 ? 'M' : 'K';
	const scale = abs >= 1_000_000 ? 1_000_000 : 1000;
	/* one decimal, truncated toward zero so the sign survives a negative delta */
	const tenths = Math.floor((abs / scale) * 10) / 10;
	const body = tenths % 1 === 0 ? String(tenths) : tenths.toFixed(1);
	return `${n < 0 ? '-' : ''}${body}${unit}`;
};

/**
 * The badge pair: a 24h delta and a pending queue.
 *
 * Shared by the PARENT rows and the sub-rows so the two can never drift apart —
 * the same fact has to look the same wherever it is shown, or the colours stop
 * being a language and become decoration.
 */
const RowBadges = ({
	badge,
}: {
	badge?: {
		delta?: number | null;
		deltaMore?: boolean;
		pending?: number | null;
	};
}) => {
	if (!badge) return null;
	const showDelta =
		badge.delta !== null && badge.delta !== undefined && badge.delta !== 0;
	const showPending =
		badge.pending !== null && badge.pending !== undefined && badge.pending > 0;
	if (!showDelta && !showPending) return null;
	/* BOTH SLOTS ARE ALWAYS RENDERED, one of them possibly empty.
	   The group is right-aligned as a unit, so with only the pills that exist
	   in the DOM a row carrying just a blue pill slid it into the column where
	   the amber one sits on the row above — the numbers stepped left and right
	   down the sidebar as each row's pair happened to differ. Holding the empty
	   column open is what puts every blue pill on one vertical line and every
	   amber pill on another. */
	const d = badge.delta as number;
	/* Signed explicitly: a bare "5" is ambiguous once a fall is possible, and
	   toLocaleString would drop the leading +. */
	const sign = d > 0 ? '+' : '';
	return (
		<span className="toggle--cnts">
			{showDelta ? (
				<span
					className="toggle--cnt toggle--cnt--delta"
					title={`${sign}${d.toLocaleString()}${
						badge.deltaMore ? '+' : ''
					} in the last 24 hours`}
				>
					{`${sign}${compactCount(d)}${badge.deltaMore ? '+' : ''}`}
				</span>
			) : (
				<span className="toggle--cnt toggle--cnt--slot" aria-hidden="true" />
			)}
			{showPending ? (
				<span
					className="toggle--cnt toggle--cnt--pending"
					title={`${(badge.pending as number).toLocaleString()} pending`}
				>
					{compactCount(badge.pending as number)}
				</span>
			) : (
				<span className="toggle--cnt toggle--cnt--slot" aria-hidden="true" />
			)}
		</span>
	);
};

export const ToggleRoute = (props: AppProps) => {
	const {
		index,
		route,
		handleChangeTab,
		pathname,
		openToggle,
		setOpenToggle,
		setisActive,
		badges = {},
	} = props;

	const { role, developer, sandboxStatus } = useRecoilValue(loginState);
	const userRolesPermissions = useRecoilValue(UserRolesPermissions);

	const { customUserTab } = useUserRoles();

	const toggleIconclasss = classNames(
		'ri-arrow-down-s-line toggle-description-icon ',
		{
			'ri-arrow-up-s-line': openToggle === route.TITLE,
		}
	);

	const handleOpen = useCallback(() => {
		if (route.TITLE === openToggle) {
			setOpenToggle('');
		}
	}, [openToggle, route.TITLE, setOpenToggle]);

	const conditionalActiveTab = useCallback(
		(tabName: string) => {
			/* A key may unlock MORE THAN ONE row: eVault's two pages share the
			   one `evault` permission, so SUB_SIDEBAR maps it to a list (see the
			   note on SUB_SIDEBAR). `concat` flattens both shapes — a string
			   stays a one-element list — so every single-row key behaves exactly
			   as it did.

			   The own-property guard is kept: `customUserTab` is server data, so
			   a key like `constructor` or `toString` would otherwise walk the
			   prototype and hand `concat` a function to spread. */
			const isAllow = (customUserTab ?? []).some(
				(key: string) =>
					Object.prototype.hasOwnProperty.call(SUB_SIDEBAR, key) &&
					([] as string[]).concat(SUB_SIDEBAR[key] ?? []).includes(tabName)
			);

			return isAllow ?? false;
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[developer, role, customUserTab, userRolesPermissions?.fullAccess]
	);

	/**
	 * Which sub-row the current URL belongs to — the LONGEST of the group's paths
	 * that the pathname is at or under, rather than each row testing itself.
	 *
	 * Two reasons it cannot be a per-row check:
	 *
	 * - The row's path is not `/${ROUTE}`. ROUTE is a PERMISSION KEY, and
	 *   Transaction Monitoring's are camelCase and nested, so `/transactionMonitoring`
	 *   is not a URL the app ever shows and none of its rows could ever highlight.
	 *   `pathForTab` resolves the real one (see TAB_PATH in routes/constants).
	 * - A prefix test alone lights up two rows at once. Triage is
	 *   `/transaction-monitoring`, which is a prefix of
	 *   `/transaction-monitoring/rules` — so on the rules page both would claim
	 *   the active state. Longest match picks the one the user is actually on.
	 */
	const activeSubRoute = useMemo(() => {
		let best: string | null = null;
		let bestLen = -1;
		for (const subRoute of route.SUB_ROUTE ?? []) {
			const path = pathForTab(subRoute.ROUTE);
			const hit = pathname === path || pathname.startsWith(`${path}/`);
			if (hit && path.length > bestLen) {
				best = subRoute.ROUTE;
				bestLen = path.length;
			}
		}
		return best;
	}, [route.SUB_ROUTE, pathname]);

	const subRouteMemo = useMemo(() => {
		return route.SUB_ROUTE.map(subRoute => {
			if (conditionalActiveTab(subRoute.TITLE)) {
				return sandboxStatus && subRoute.TITLE === 'Embed Web' ? (
					<></>
				) : (
					<li
						key={subRoute.ROUTE}
						className={
							subRoute.ROUTE === activeSubRoute ? 'toggle--pad--active' : ''
						}
						onClick={() => {
							handleChangeTab(subRoute.ROUTE);
						}}
					>
						{subRoute.TITLE}
						<RowBadges badge={badges[subRoute.TITLE]} />
					</li>
				);
			}
			return <></>;
		});
	}, [
		route.SUB_ROUTE,
		conditionalActiveTab,
		activeSubRoute,
		sandboxStatus,
		handleChangeTab,
		badges,
	]);

	return (
		<div
			className="toggle-description"
			key={index}
			onClick={() => {
				if (route.TITLE !== openToggle) {
					setOpenToggle(route.TITLE);
				}
			}}
		>
			<div className="toggle--main" onClick={handleOpen}>
			<i 
  				className={`sidebar__icon  ${setisActive ? `${route.ICON}fill` : `${route.ICON}line`}`} 
  				id={route.ICON} 
			/>

				<span className="toggle-title">{route.TITLE}</span>
				{/* PARENT ROWS CARRY THE ROLL-UP. Onboarding and eSign each summarise
				    their group, so the numbers are visible with the group collapsed —
				    which is how the sidebar sits most of the time. */}
				{/* Badges then chevron, so the arrow stays the rightmost thing on the
				    row exactly as it is on Settings and Integrate. The chevron's own
				    `margin-left: auto` (sidebar-v2.scss) is cancelled in the scss when
				    a badge group precedes it — two auto margins on one flex line split
				    the free space between them, which left the arrow stranded beside
				    the label and the numbers adrift. */}
				<RowBadges badge={badges[route.TITLE]} />
				<i className={toggleIconclasss} />
			</div>

			{openToggle === route.TITLE && (
				<div className="toggle--sub">
					<ul className="toggle--pad">{subRouteMemo}</ul>
				</div>
			)}
		</div>
	);
};
